// ===========================================
// Dashboard API Functions
// ===========================================
// API calls for dashboard statistics endpoints

import { apiClient } from '../services/api';
import type { EnquiryFlagKey } from './enquiry.api';

// ===========================================
// TYPES
// ===========================================

export interface DriveSyncStatus {
  isConnected: boolean;
  lastSyncAt: string | null;
  syncedFoldersCount: number;
  errorCount: number;
  status: 'healthy' | 'warning' | 'error' | 'disconnected';
}

export interface AdminDashboardStats {
  totalActiveStudents: number;
  totalActiveFamilies: number;
  totalActiveTeachers: number;
  totalLessonsThisWeek: number;
  outstandingTeacherTasks: number;
  attendanceRateThisWeek: number;
  attendanceRateThisMonth: number;
  /** In cents. NULL for a caller without operations_invoices.read (roles R16). */
  totalOutstandingPayments: number | null;
  pendingMeetAndGreets: number | null;
  upcomingMeetAndGreets: number | null;
  /** NULL without enquiries.read (D4). */
  pendingEnquiries: number | null;
  /** Flag counts (null per flag = off); the whole field is null without enquiries.read (plan §10). */
  enquiryFlags: Record<EnquiryFlagKey, number | null> | null;
  pendingAbsences: number;
  driveSyncStatus: DriveSyncStatus;
  /** Student birthdays in the school's current week (1 Jan placeholders excluded). */
  birthdaysThisWeek: number;
  /** Students still recorded with the unknown-birth-date sentinel. */
  studentsMissingBirthDate: number;
}

export interface BirthdayClass {
  lessonId: string;
  lessonName: string;
  startTime: string | null;
  /** The class actually runs on the birthday (occurrence-aware, cancellations excluded). */
  onTheirLessonDay: boolean;
}

export interface BirthdayStudent {
  studentId: string;
  firstName: string;
  lastName: string;
  /** `YYYY-MM-DD` of the birthday within the week. */
  dateKey: string;
  ageTurning: number;
  classes: BirthdayClass[];
}

export interface WeeklyBirthdays {
  weekStart: string;
  weekEnd: string;
  students: BirthdayStudent[];
}

export interface TeacherDashboardStats {
  totalLessonsThisWeek: number;
  totalStudents: number;
  attendanceRateThisWeek: number;
  pendingNotesCount: number;
  recentlyUploadedFiles: number;
  assignedMeetAndGreets: number;
}

export interface ParentDashboardStats {
  childrenCount: number;
  upcomingLessons: number;
  outstandingInvoices: number;
  outstandingAmount: number; // In cents
  sharedFilesCount: number;
  openBookingPeriods: number;
}

export type ParentNotificationType = 'LESSON_NOTE' | 'INVOICE' | 'PAYMENT' | 'HYBRID_BOOKING' | 'HYBRID_BOOKING_OPEN' | 'HYBRID_BOOKING_RESCHEDULED' | 'HYBRID_BOOKING_CANCELLED' | 'FILE_UPLOAD' | 'LESSON_CANCELLED' | 'ABSENCE_REPORTED' | 'ABSENCE_CANCELLED' | 'MAKEUP_CREDIT' | 'EVENT_PUBLISHED'
  // ⚠️ SINGLE TOKEN, deliberately. `extractTypeAndTargetId` in NotificationsCard
  // splits the composite id on '_', so a two-word type like PERMISSION_SLIP
  // would parse as type 'PERMISSION' / targetId 'SLIP_<cuid>' and write a
  // dismissal row that never matches the generated id.
  | 'PERMISSIONSLIP';

export interface ParentNotification {
  id: string; // Format: `${type}_${targetId}`
  type: ParentNotificationType;
  title: string;
  message: string;
  targetId: string;
  studentId?: string;
  studentName?: string;
  createdAt: string;
  navigateTo: string;
  // Additional fields for LESSON_NOTE type (to display in modal)
  noteContent?: string;
  lessonName?: string;
  authorName?: string;
  instrumentName?: string;
  instrumentColor?: string;
  noteVisibility?: 'PARENTS' | 'STUDENTS'; // For visibility marker
}

export type ActivityType =
  | 'enrollment'
  | 'payment'
  | 'booking'
  | 'booking_rescheduled'
  | 'booking_cancelled'
  | 'attendance'
  | 'file_upload'
  | 'meet_and_greet'
  | 'makeup_eligibility'
  | 'makeup_booking'
  | 'replacement_teacher'
  | 'cancellation_lesson'
  | 'reschedule_lesson'
  | 'extra_lesson_request'
  | 'absence_notification'
  | 'absence_cancelled'
  | 'event_published'
  | 'teacher_time_off'
  | 'permission_slip_signed'
  | 'permission_slip_declined';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface RecentFile {
  id: string;
  fileName: string;
  mimeType: string;
  createdAt: string;
  lessonName?: string;
  studentName?: string;
}

export interface PendingMeetAndGreet {
  id: string;
  studentName: string;
  status: 'PENDING_VERIFICATION' | 'PENDING_APPROVAL' | 'APPROVED' | 'WAITLISTED' | 'CONVERTED';
  scheduledDateTime: string | null;
  createdAt: string;
  /** Teacher dashboard only (#255): the meeting has ended and no notes are in (the server's verdict). */
  notesMissing?: boolean;
}

// ===========================================
// RESPONSE WRAPPER TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// API FUNCTIONS
// ===========================================

export const dashboardApi = {
  // Admin endpoints
  getAdminStats: async (): Promise<AdminDashboardStats> => {
    const response = await apiClient.get<ApiResponse<AdminDashboardStats>>(
      '/dashboard/admin/stats'
    );
    return response.data;
  },

  /**
   * The week's birthday list. Sits behind `people_students.read` — the bare
   * count on the card rides the stats object, which is behind `dashboard.read`.
   */
  getWeeklyBirthdays: async (weekStart?: string): Promise<WeeklyBirthdays> => {
    const response = await apiClient.get<ApiResponse<WeeklyBirthdays>>(
      '/dashboard/admin/birthdays',
      weekStart ? { params: { weekStart } } : undefined
    );
    return response.data;
  },

  getActivityFeed: async (limit = 10): Promise<ActivityItem[]> => {
    const response = await apiClient.get<ApiResponse<ActivityItem[]>>(
      '/dashboard/admin/activity-feed',
      { params: { limit } }
    );
    return response.data;
  },

  getDriveSyncStatus: async (): Promise<DriveSyncStatus> => {
    const response = await apiClient.get<ApiResponse<DriveSyncStatus>>(
      '/dashboard/admin/drive-sync-status'
    );
    return response.data;
  },

  getPendingMeetAndGreets: async (limit = 10): Promise<PendingMeetAndGreet[]> => {
    const response = await apiClient.get<ApiResponse<PendingMeetAndGreet[]>>(
      '/dashboard/admin/pending-meet-and-greets',
      { params: { limit } }
    );
    return response.data;
  },

  // Teacher endpoints
  getTeacherStats: async (): Promise<TeacherDashboardStats> => {
    const response = await apiClient.get<ApiResponse<TeacherDashboardStats>>(
      '/dashboard/teacher/stats'
    );
    return response.data;
  },

  getTeacherRecentFiles: async (limit = 5): Promise<RecentFile[]> => {
    const response = await apiClient.get<ApiResponse<RecentFile[]>>(
      '/dashboard/teacher/recent-files',
      { params: { limit } }
    );
    return response.data;
  },

  getAssignedMeetAndGreets: async (limit = 10): Promise<PendingMeetAndGreet[]> => {
    const response = await apiClient.get<ApiResponse<PendingMeetAndGreet[]>>(
      '/dashboard/teacher/assigned-meet-and-greets',
      { params: { limit } }
    );
    return response.data;
  },

  // Parent endpoints
  getParentStats: async (): Promise<ParentDashboardStats> => {
    const response = await apiClient.get<ApiResponse<ParentDashboardStats>>(
      '/dashboard/parent/stats'
    );
    return response.data;
  },

  getParentNotifications: async (): Promise<ParentNotification[]> => {
    const response = await apiClient.get<ApiResponse<ParentNotification[]>>(
      '/dashboard/parent/notifications'
    );
    return response.data;
  },

  dismissParentNotification: async (type: string, targetId: string): Promise<void> => {
    await apiClient.post('/dashboard/parent/notifications/dismiss', { type, targetId });
  },
};
